/**
 * MenuScene - Main menu: continue a run in progress, start a new run,
 * adjust settings, and view lifetime stats.
 */

import Phaser from 'phaser';
import { GameStateManager } from '../game/GameStateManager';
import { SCENES, GAME_CONFIG } from '../constants/GameConstants';
import { AudioManager } from '../game/AudioManager';
import { T, INK, PALETTE, FONT, makeButton, paintBackground, makePanel, fadeInScene } from '../constants/Theme';

export class MenuScene extends Phaser.Scene {
  private gameStateManager!: GameStateManager;

  constructor() {
    super({ key: SCENES.MENU });
  }

  init(): void {
    this.gameStateManager = GameStateManager.getInstance();
  }

  create(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const cx = WIDTH / 2;

    AudioManager.getInstance().init(this);
    paintBackground(this, WIDTH, HEIGHT);
    fadeInScene(this);

    // Crest: a gold king glyph above the title
    const crest = this.add.text(cx, HEIGHT * 0.20, '♚', {
      fontFamily: FONT,
      fontSize: '150px',
      color: INK.gold,
    });
    crest.setOrigin(0.5);
    crest.setShadow(0, 6, '#0a0f1c', 14, false, true);

    // Title
    const title = this.add.text(cx, HEIGHT * 0.34, 'CHESS ROGUE', T.display());
    title.setOrigin(0.5);
    title.setLetterSpacing(2);
    title.setShadow(0, 4, '#0a0f1c', 10, false, true);

    const subtitle = this.add.text(cx, HEIGHT * 0.34 + 70, 'A ROGUELIKE CHESS ADVENTURE', T.subheading({
      color: INK.gold,
    }));
    subtitle.setOrigin(0.5);
    subtitle.setLetterSpacing(4);

    const hasSave = this.gameStateManager.hasSavedRun();
    let buttonY = HEIGHT * 0.56;
    const gap = 100;

    if (hasSave) {
      makeButton(this, cx, buttonY, 'CONTINUE', {
        fill: PALETTE.green, onClick: () => this.continueRun(),
      });
      buttonY += gap;
      makeButton(this, cx, buttonY, 'NEW RUN', {
        fill: PALETTE.blue, onClick: () => this.confirmNewGame(),
      });
      buttonY += gap;
    } else {
      makeButton(this, cx, buttonY, 'NEW RUN', {
        fill: PALETTE.blue, onClick: () => this.startNewGame(),
      });
      buttonY += gap;
    }

    makeButton(this, cx, buttonY, 'SETTINGS', {
      fill: PALETTE.panelRaised, onClick: () => this.scene.start(SCENES.SETTINGS, { from: SCENES.MENU }),
    });

    // Lifetime stats pill
    const stats = this.gameStateManager.getLifetimeStats();
    if (stats.runsPlayed > 0) {
      const pillY = HEIGHT - 150;
      makePanel(this, cx, pillY, 560, 64, { fill: PALETTE.panel, radius: 32, alpha: 0.85 });
      const statsText = this.add.text(
        cx, pillY,
        `★ ${stats.runsWon} wins   ·   ${stats.runsPlayed} runs   ·   Best floor ${stats.bestFloor}`,
        T.small({ color: INK.secondary })
      );
      statsText.setOrigin(0.5);
    } else {
      const hint = this.add.text(
        cx, HEIGHT - 150,
        'Defeat enemy armies with chess tactics.\nCollect upgrades to grow stronger each floor.',
        T.small({ align: 'center', color: INK.faint, lineSpacing: 6 })
      );
      hint.setOrigin(0.5);
    }

    const version = this.add.text(WIDTH - 16, HEIGHT - 14, 'v1.0.0', T.micro());
    version.setOrigin(1, 1);
  }

  private continueRun(): void {
    if (this.gameStateManager.loadGame()) {
      this.scene.start(SCENES.MAP);
    } else {
      this.startNewGame();
    }
  }

  private confirmNewGame(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const cx = WIDTH / 2;

    const overlay = this.add.rectangle(0, 0, WIDTH, HEIGHT, 0x0a0f1c, 0.8).setOrigin(0);
    overlay.setDepth(200);
    overlay.setInteractive();

    const panel = makePanel(this, cx, HEIGHT / 2, 600, 420, {
      fill: PALETTE.panel, stroke: PALETTE.gold, strokeWidth: 2, radius: 24,
    });
    panel.setDepth(201);

    const prompt = this.add.text(cx, HEIGHT / 2 - 120,
      'Abandon your run in progress\nand start over?', T.heading({ align: 'center', lineSpacing: 8 }));
    prompt.setOrigin(0.5);
    prompt.setDepth(202);

    const els: Phaser.GameObjects.GameObject[] = [overlay, panel, prompt];

    const yes = makeButton(this, cx, HEIGHT / 2 + 10, 'START OVER', {
      fill: PALETTE.red, depth: 202, onClick: () => this.startNewGame(),
    });
    const no = makeButton(this, cx, HEIGHT / 2 + 110, 'CANCEL', {
      fill: PALETTE.panelRaised, depth: 202, onClick: () => {
        els.forEach(e => e.destroy());
        yes.container.destroy();
        no.container.destroy();
      },
    });
  }

  private startNewGame(): void {
    this.gameStateManager.startNewRun();
    this.scene.start(SCENES.MAP);
  }
}
